import { resolveDomainEventOrDecision } from './workflowSpec.js'
import { getEventColorByName, EventColorNames } from '../constants/eventColors.js'
import {
  computeStableOffsets,
  computeGridPositions,
  reconcileEventGroupIds,
  recalculateGroupSlots
} from './diagramLayout.js'

export const buildUpdateEventMutation = ({
  workflow,
  workflowId,
  eventRef,
  description,
  lane,
  color,
  conditionLabel,
  group,
  type
}) => {
  if (!eventRef) {
    return { error: 'Domain event ref is required' }
  }

  const resolvedEvent = resolveDomainEventOrDecision(eventRef, workflow)
  if (!resolvedEvent) {
    return { error: `Domain event or decision not found for ref: ${eventRef}` }
  }
  const eventId = resolvedEvent.id

  const domainEvents = workflow.domainEvents || []
  const lanes = workflow.lanes || []
  const eventIndex = domainEvents.findIndex((e) => e.id === eventId)

  if (eventIndex === -1) {
    return { error: 'Event not found' }
  }

  const event = domainEvents[eventIndex]
  const changes = []

  const eventInput = {
    id: event.id,
    index: eventIndex,
    expectedVersion: event.version || undefined
  }
  let hasPropertyChanges = false

  if (event.laneId) {
    eventInput.laneIndex = lanes.findIndex((l) => l.id === event.laneId)
  }

  if (lane !== undefined) {
    const newLane = lanes.find((l) => l.name === lane)
    if (!newLane) {
      return { error: `Lane "${lane}" not found. Use get_workflow to find available lanes.` }
    }
    const newLaneIndex = lanes.findIndex((l) => l.id === newLane.id)
    eventInput.laneId = newLane.id
    eventInput.laneIndex = newLaneIndex
    changes.push(`moved to lane "${lane}"`)
    hasPropertyChanges = true
  }

  if (description !== undefined) {
    const trimmedDescription = typeof description === 'string' ? description.trim() : ''
    if (!trimmedDescription) {
      return { error: 'Event description cannot be blank.' }
    }
    eventInput.description = trimmedDescription
    changes.push(`description updated to "${trimmedDescription}"`)
    hasPropertyChanges = true
  }

  if (color !== undefined) {
    const colorData = getEventColorByName(color)
    if (!colorData) {
      return { error: `Invalid color "${color}". Valid colors: ${EventColorNames.join(', ')}` }
    }
    eventInput.color = colorData
    changes.push(`color updated to "${color}"`)
    hasPropertyChanges = true
  }

  if (conditionLabel !== undefined) {
    if (conditionLabel) {
      const gatewayParents = (event.parents || []).filter((parentId) => {
        const parentEvent = domainEvents.find((domainEvent) => domainEvent.id === parentId)

        return parentEvent?.type === 'bpmn:ExclusiveGateway'
      })

      if (gatewayParents.length === 0) {
        return { error: `Cannot set a condition label on "${event.description}": it has no decision parent, so there is no branch to label.` }
      }

      eventInput.conditionLabel = null
      eventInput.conditionLabels = gatewayParents.map((parentId) => ({
        parentId,
        name: conditionLabel
      }))
      changes.push(`conditionLabel set to "${conditionLabel}"`)
    } else {
      eventInput.conditionLabel = null
      eventInput.conditionLabels = null
      changes.push('conditionLabel cleared')
    }
    hasPropertyChanges = true
  }

  const extraEventInputs = []
  const offsetUpdates = []
  let updatedGroupsInput = null

  if (group !== undefined) {
    const groups = workflow.groups || []
    let targetGroupId = null
    let isFirstGroupRequest = false

    if (group) {
      const matchedGroup = groups.find((g) => g.name === group)
      if (!matchedGroup) {
        return { error: `Group "${group}" not found. Use get_workflow to see available groups.` }
      }
      targetGroupId = matchedGroup.id

      const sortedGroups = [...groups].sort((a, b) => a.startSlotX - b.startSlotX)
      const firstGroup = sortedGroups[0]
      if (firstGroup && firstGroup.id === targetGroupId) {
        targetGroupId = null
        isFirstGroupRequest = true
      }
    }

    const simulatedEvents = domainEvents.map((existingEvent) => {
      if (existingEvent.id === eventId) {
        return { ...existingEvent, groupId: targetGroupId }
      }

      return existingEvent
    })

    const reconciliationUpdates = reconcileEventGroupIds(simulatedEvents)
    const reconciliationMap = new Map(reconciliationUpdates.map((u) => [u.id, u.groupId]))

    const finalTargetGroupId = reconciliationMap.has(eventId)
      ? reconciliationMap.get(eventId)
      : targetGroupId

    eventInput.groupId = finalTargetGroupId
    hasPropertyChanges = true

    if (group) {
      if (finalTargetGroupId || isFirstGroupRequest) {
        changes.push(`assigned to group "${group}"`)
      } else {
        changes.push(`already in group "${group}" via parent chain`)
      }
    } else {
      changes.push('removed from group')
    }

    for (const update of reconciliationUpdates) {
      if (update.id === eventId) {
        continue
      }
      const otherIndex = domainEvents.findIndex((e) => e.id === update.id)
      if (otherIndex === -1) {
        continue
      }
      const otherEvent = domainEvents[otherIndex]
      extraEventInputs.push({
        id: otherEvent.id,
        index: otherIndex,
        groupId: update.groupId,
        expectedVersion: otherEvent.version || undefined
      })
    }

    const eventsForSlotCalc = simulatedEvents.map((existingEvent) => {
      if (reconciliationMap.has(existingEvent.id)) {
        return { ...existingEvent, groupId: reconciliationMap.get(existingEvent.id) }
      }

      return existingEvent
    })
    updatedGroupsInput = recalculateGroupSlots(eventsForSlotCalc, groups)

    // Refuse an assignment that would push the event to the right of its own follow-up events.
    const simulatedPositions = computeGridPositions({
      domainEvents: eventsForSlotCalc,
      lanes: workflow.lanes,
      groups: updatedGroupsInput || groups
    })
    for (const candidate of eventsForSlotCalc) {
      const candidateColumn = simulatedPositions[candidate.id]?.[0]
      if (candidateColumn === undefined || (candidate.parents || []).includes('start')) {
        continue
      }
      const parentColumns = (candidate.parents || [])
        .map((parentId) => simulatedPositions[parentId]?.[0])
        .filter((column) => column !== undefined)
      const hasParentOnLeft = parentColumns.some((column) => column < candidateColumn)
      const hasParentNotOnLeft = parentColumns.some((column) => column >= candidateColumn)
      if (!hasParentOnLeft && hasParentNotOnLeft) {
        return {
          error: `Cannot assign "${event.description}" to group "${group}": it would place the event after events that follow it, breaking the diagram's left-to-right flow. An event can only join a group at or ahead of its own position in the flow.`
        }
      }
    }

    // Re-lay-out vertical offsets the way the frontend does: moving an event into a group can pull
    // a downstream event far right, leaving a node sitting on the now-long connection line.
    const stableOffsets = computeStableOffsets({
      domainEvents: eventsForSlotCalc,
      lanes: workflow.lanes,
      groups: updatedGroupsInput || groups
    })
    for (const [index, existingEvent] of domainEvents.entries()) {
      const newOffset = stableOffsets[existingEvent.id]
      if (newOffset !== undefined && newOffset !== (existingEvent.offset || 0)) {
        offsetUpdates.push({
          id: existingEvent.id,
          index,
          offset: newOffset,
          expectedVersion: existingEvent.version || undefined
        })
      }
    }
  }

  if (type !== undefined) {
    const TYPE_ALIASES = {
      domainEvent: 'bpmn:Task',
      decision: 'bpmn:ExclusiveGateway'
    }
    const targetType = TYPE_ALIASES[type]
    if (!targetType) {
      return { error: `Invalid type "${type}". Must be one of: domainEvent, decision` }
    }

    if (event.type !== targetType) {
      eventInput.type = targetType
      changes.push(targetType === 'bpmn:ExclusiveGateway' ? 'changed to a decision' : 'changed to an event')
      hasPropertyChanges = true

      // Converting to a non-gateway removes the branch conditions its children carried for it
      // (mirrors the frontend's clearChildConditionLabelsForParent in transformShape).
      if (targetType !== 'bpmn:ExclusiveGateway') {
        for (const [childIndex, childEvent] of domainEvents.entries()) {
          if (!(childEvent.parents || []).includes(eventId)) {
            continue
          }
          const labels = childEvent.conditionLabels || []
          const remaining = labels.filter((label) => label.parentId !== eventId)
          const clearsLabels = remaining.length !== labels.length
          const clearsFlag = childEvent.parents?.[0] === eventId && childEvent.showConditionLabelOnEvent === true
          if (!clearsLabels && !clearsFlag) {
            continue
          }
          const childUpdate = extraEventInputs.find((input) => input.id === childEvent.id) || {
            id: childEvent.id,
            index: childIndex,
            expectedVersion: childEvent.version || undefined
          }
          if (clearsLabels) {
            childUpdate.conditionLabels = remaining.length ? remaining : null
          }
          if (clearsFlag) {
            childUpdate.showConditionLabelOnEvent = false
          }
          if (!extraEventInputs.includes(childUpdate)) {
            extraEventInputs.push(childUpdate)
          }
        }
      }
    }
  }

  let mutationInput = null

  if (hasPropertyChanges) {
    const eventInputs = [eventInput, ...extraEventInputs]
    for (const update of offsetUpdates) {
      const existing = eventInputs.find((e) => e.id === update.id)
      if (existing) {
        existing.offset = update.offset
      } else {
        eventInputs.push({
          id: update.id,
          index: update.index,
          offset: update.offset,
          expectedVersion: update.expectedVersion
        })
      }
    }
    mutationInput = {
      id: workflowId,
      expectedVersion: workflow.version,
      domainEvents: eventInputs,
      updateNote: {
        action: 'updated event',
        target: event.description || eventId
      }
    }
    if (updatedGroupsInput) {
      mutationInput.groups = updatedGroupsInput
    }
  }

  return {
    event,
    eventId,
    eventIndex,
    changes,
    hasPropertyChanges,
    mutationInput
  }
}
